import { AccountStatusEnum } from '@app/app.enum';
import { history } from '@app/app.routes';
import store from '@app/app.store';
import * as maerskLogo from '@app/assets/images/maersk-logo-rgb.svg';
import NavigationBackButton from '@app/components/core/navigationbackbutton.component';
import Icon from '@app/components/shared/icon.component';
import environment from '@app/environments/environment';
import { IMenuInjectedProps, IModuleType, initialHeaderState, Module } from '@app/models/core.models';
import * as models from '@app/models/index';
import { withAppModule } from '@app/providers/app-module.provider';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { AppBar, Box, Breadcrumbs, Hidden, IconButton, Menu, MenuItem, Toolbar, Typography } from '@material-ui/core';
import classNames from 'classnames';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import * as React from 'react';
import { withRouter } from 'react-router-dom';
import { Unsubscribe } from 'redux';

const accountService: AccountService = Injector.get(AccountService);
const intlService: IntlService = Injector.get(IntlService);

class HeaderComponent extends React.Component<models.IHeaderComponentProps, models.IHeaderComponentState> {
	private unsubscribe: Unsubscribe;

	constructor(props: models.IHeaderComponentProps) {
		super(props);
		this.state = initialHeaderState() as models.IHeaderComponentState;
		this.bindFunctions();
	}

	public componentDidMount() {
		const isClosed = this.state.status === AccountStatusEnum.Closed;
		const onAccountClosed = (isClosedAccount: boolean) => {
			this.setState({
				isClosed: isClosedAccount,
				status: store.getState().authentication.status,
			});
		};
		this.unsubscribe = store.subscribe(() =>
			onAccountClosed(store.getState().authentication.status === AccountStatusEnum.Closed)
		);
		onAccountClosed(isClosed);
	}

	public componentWillUnmount() {
		this.unsubscribe();
	}

	public componentDidUpdate(prevProps: models.IHeaderComponentProps, prevState: models.IHeaderComponentState) {
		if (prevState.status !== this.state.status && this.state.status === AccountStatusEnum.Closed) {
			this.setState({
				isClosed: true,
			});
		}
	}

	public render() {
		const renderMenu = (
			<PopupState variant='popper'>
				{(popupState: IMenuInjectedProps) => (
					<React.Fragment>
						<IconButton {...bindTrigger(popupState)} className='icon-button'>
							<Icon name='ellipsis-vertical' className='icon-dimension-square' />
						</IconButton>
						<Menu {...bindMenu(popupState)}>
							{this.props.AppModule.modules
								.filter(
									(x) =>
										x.location === 'header' &&
										x.accountIsClosedValues.includes(this.state.status === AccountStatusEnum.Closed)
								)
								.map((module) => (
									<MenuItem key={module.name} onClick={() => this.onMenuItemClick(popupState, module)}>
										{module.toString()}
									</MenuItem>
								))}
						</Menu>
					</React.Fragment>
				)}
			</PopupState>
		);

		return (
			<React.Fragment>
				<AppBar color='inherit' elevation={0} position='fixed' className='header-bar'>
					<Toolbar>
						<Hidden smUp={true}>
							<IconButton onClick={this.onSidebarToggle} className='icon-button'>
								<Icon name='bars-horizontal' style={{ transform: 'translateY(-2.1px)' }} />
							</IconButton>
						</Hidden>
						<Hidden xsDown={true}>
							<Box
								alignItems='center'
								display='flex'
								className={`header-logo ${this.props.sidebarOpen ? 'open' : 'close'}`}
							>
								<img src={maerskLogo} className='genesis-logo' />
							</Box>
						</Hidden>
						<NavigationBackButton />
						<Breadcrumbs
							className='header-breadcrumb'
							color='inherit'
							separator={<Icon name='chevron-right' fontSize='small' style={{ transform: 'translateY(1.2px)' }} />}
							aria-label='breadcrumb'
						>
							{this.getBreadCrumb(this.props.AppModule.module).map((breadcrumb) => (
								<Typography
									className={classNames({ close: !this.props.sidebarOpen })}
									key={breadcrumb.title}
									color='inherit'
									style={{ cursor: 'pointer' }}
									onClick={() => {
										history.push(breadcrumb.url, { ...breadcrumb.state });
									}}
								>
									{breadcrumb.title}
								</Typography>
							))}
						</Breadcrumbs>
						<div className='flex-spacer' />
						<Typography>
							{this.state.isClosed
								? intlService.GetFormattedMessage('app.profile.user-info-form.account-closed-label')
								: null}
						</Typography>
						<IconButton className='icon-button' onClick={() => window.open(environment.apiReferenceDocUrl, '_blank')}>
							<Icon name='question-circle' />
						</IconButton>
						{renderMenu}
					</Toolbar>
				</AppBar>
			</React.Fragment>
		);
	}

	private signOut() {
		accountService.signOut();
	}

	private onMenuItemClick(popupState: IMenuInjectedProps, module: IModuleType) {
		popupState.close();

		switch (module) {
			case Module.Billing:
				history.push(module.url);
				break;

			case Module.Profile:
				history.push(module.url);
				break;

			case Module.User:
				history.push(module.url);
				break;

			case Module.SignOut:
				this.signOut();
				this.props.onUserSignOut();
				break;
		}
	}

	private getBreadCrumb(module: IModuleType) {
		const breadCrumbArray: models.IBreadCrumb[] = [{ title: module.toString(), url: module.url, state: null }];

		if (this.props.location?.state?.breadcrumb) {
			const breadCrumbData = Array.isArray(this.props.history.location.state.breadcrumb)
				? this.props.history.location.state.breadcrumb
				: [this.props.history.location.state.breadcrumb];
			for (let i = 0; i < breadCrumbData.length; i++) {
				breadCrumbArray.push({
					title: breadCrumbData[i].title,
					// and below property is used for that purpose only
					url: breadCrumbData[i + 1]?.location?.pathname,
					state: {
						...breadCrumbData[i + 1]?.location?.state,
						breadcrumb: breadCrumbData.slice(0, i + 1),
					},
				});
			}
		}

		return breadCrumbArray;
	}

	private onSidebarToggle() {
		this.props.onSidebarToggle();
	}

	private bindFunctions() {
		this.onSidebarToggle = this.onSidebarToggle.bind(this);
		this.onMenuItemClick = this.onMenuItemClick.bind(this);
		this.getBreadCrumb = this.getBreadCrumb.bind(this);
	}
}

export default withRouter(withAppModule(HeaderComponent));



// WEBPACK FOOTER //
// ./src/components/core/header/header.component.tsx